import type { IncidentFeature } from "@sikhio/shared";
import { PanelHeader } from "./PanelHeader";
import { hourlyLoad, zoneOccupancy, type ParkingZone } from "../lib/pmcu";

interface Props {
  hour: number;
  isWeekend: boolean;
  iticEvents: IncidentFeature[];
  cityReports: IncidentFeature[];
  trafficSampleCount: number;
}

interface Corridor {
  id: string;
  name: string;
  base: number;
}
const CORRIDORS: Corridor[] = [
  { id: "mittraphap", name: "Mittraphap Hwy 2",  base: 0.65 },
  { id: "hwy201",     name: "Hwy 201 Sikhio–CYP", base: 0.45 },
  { id: "hwy205",     name: "Hwy 205 Sikhio–DKT", base: 0.38 },
  { id: "towncentre", name: "Town Centre Ring",   base: 0.42 },
];


const PARKING_ZONES: ParkingZone[] = [
  { id: "P1", name: "Municipal Hall · North",   capacity: 180 },
  { id: "P2", name: "Day Market · Central",     capacity: 320 },
  { id: "P3", name: "Night Market · West",      capacity: 200 },
  { id: "P4", name: "Hospital Complex · South", capacity: 240 },
];


interface FleetEntry {
  id: string;
  label: string;
  count: number;
  unit: string;
  note: string;
}
const FLEET: FleetEntry[] = [
  { id: "muni-truck", label: "Municipal Truck", count: 8,  unit: "trucks",   note: "waste + maintenance" },
  { id: "songthaew",  label: "Songthaew",       count: 28, unit: "vehicles", note: "town–station routes" },
  { id: "tuk-tuk",    label: "Tuk-tuk",         count: 55, unit: "vehicles", note: "market + Hwy 2 stops" },
  { id: "moto-taxi",  label: "Moto-taxi",        count: 90, unit: "drivers", note: "licensed stands" },
];

interface Development {
  id: string;
  name: string;
  status: "open" | "in-progress" | "planned";
  describe: string;
}
const DEVELOPMENTS: Development[] = [
  { id: "smart-city",    name: "Smart City Hub",         status: "in-progress", describe: "DEPA-backed IoT + open data platform for Sikhio" },
  { id: "flood-infra",   name: "Lam Takhong Levee",      status: "in-progress", describe: "Riverine flood retention & early-warning sensors" },
  { id: "digital-svc",   name: "Digital Services",       status: "open",        describe: "Online permits, payments, citizen reporting" },
  { id: "srt-upgrade",   name: "SRT Station Precinct",   status: "planned",     describe: "Station access road + commuter parking upgrade" },
];
const DEV_COLOR: Record<Development["status"], string> = {
  open: "var(--good)",
  "in-progress": "var(--warn)",
  planned: "var(--text-3)",
};

export function PmcuBrief({ hour, isWeekend, iticEvents, cityReports, trafficSampleCount }: Props) {
  const load = hourlyLoad(hour, isWeekend);

  const totalParkingCapacity = PARKING_ZONES.reduce((s, z) => s + z.capacity, 0);
  const totalOccupied = PARKING_ZONES.reduce((s, z) => s + Math.round(z.capacity * zoneOccupancy(z, hour, isWeekend)), 0);
  const totalOccupancyPct = Math.round((totalOccupied / totalParkingCapacity) * 100);

  const openIncidents = cityReports.filter((r) => r.status !== "resolved").length + iticEvents.length;

  return (
    <div className="pmcu-brief">
      <PanelHeader
        title="งานเทศบาล "
        fallbackTier="scenario"
        source="live-incidents·model"
        actions={
          <span
            className="mono caption data-age--warn"
            title="Arterial load and parking occupancy are sinusoidal models. Incident counts are live."
            aria-label="Partly modelled data"
          >
            PART MODELLED
          </span>
        }
      />
      {/*
        The Pmcu numbers (arterial load, parking occupancy, fleet, developments)
        are all STATIC reference values hardcoded in this file. The only live
        data is the OPEN INCIDENTS counter. Without this banner a Mayor
        skimming the panel could quote the fleet count in a council meeting
        and get caught out — the transport office knows the real figures.
        One short, plain-language line is the cheapest insurance.
      */}
      <div className="pmcu-static-notice" role="note">
        <span className="pmcu-static-notice-icon" aria-hidden>ℹ</span>
        <span>
          ข้อมูลส่วนใหญ่เป็นตัวเลขอ้างอิง ไม่ใช่ข้อมูลสด ·
          Only the OPEN INCIDENTS counter is live. The rest (arterial load,
          parking, fleet, developments) is a static reference.
        </span>
      </div>
      {/* ── Municipality overview ── */}
      <section className="pmcu-section">
        <header className="pmcu-h">
          <span className="brief-tag mono">Municipality overview</span>
          <span className="mono caption">เทศบาลเมืองสีคิ้ว</span>
        </header>
        <div className="pmcu-kv-grid">
          <div className="pmcu-kv">
            <div className="num">{trafficSampleCount || "—"}</div>
            <div className="lbl">ROAD SAMPLES</div>
          </div>
          <div className="pmcu-kv">
            <div className="num">{openIncidents}</div>
            <div className="lbl">OPEN INCIDENTS</div>
          </div>
        </div>
      </section>

      {/* ── Arterial load ── */}
      <section className="pmcu-section">
        <header className="pmcu-h">
          <span className="brief-tag mono">Arterial load</span>
          <span className="mono caption">hour {String(hour).padStart(2, "0")}{isWeekend ? " · weekend" : ""}</span>
        </header>
        <ul className="pmcu-rows">
          {CORRIDORS.map((c) => {
            const pct = Math.min(1, c.base * load * 1.4);
            const colour =
              pct > 0.8 ? "var(--bad)" : pct > 0.6 ? "var(--warn)" : pct > 0.4 ? "var(--data)" : "var(--good)";
            return (
              <li key={c.id} className="pmcu-row">
                <span className="pmcu-row-name">{c.name}</span>
                <span className="pmcu-row-bar">
                  <span className="pmcu-row-fill" style={{ width: `${Math.round(pct * 100)}%`, background: colour }} />
                </span>
                <span className="pmcu-row-val mono">{Math.round(pct * 100)}%</span>
              </li>
            );
          })}
        </ul>
        <div className="pmcu-foot mono">
          {trafficSampleCount} road samples · modeled · iTIC live: {iticEvents.length}
        </div>
      </section>

      {/* ── Parking zones ── */}
      <section className="pmcu-section">
        <header className="pmcu-h">
          <span className="brief-tag mono">Parking zones</span>
          <span className="mono caption">{totalOccupied}/{totalParkingCapacity} · {totalOccupancyPct}%</span>
        </header>
        <ul className="pmcu-rows">
          {PARKING_ZONES.map((zone) => {
            const occ = zoneOccupancy(zone, hour, isWeekend);
            const filled = Math.round(zone.capacity * occ);
            const colour = occ > 0.9 ? "var(--bad)" : occ > 0.75 ? "var(--warn)" : "var(--good)";
            return (
              <li key={zone.id} className="pmcu-row">
                <span className="pmcu-row-name" title={zone.name}>{zone.id}</span>
                <span className="pmcu-row-bar">
                  <span className="pmcu-row-fill" style={{ width: `${Math.round(occ * 100)}%`, background: colour }} />
                </span>
                <span className="pmcu-row-val mono">{filled}/{zone.capacity}</span>
              </li>
            );
          })}
        </ul>
        <div className="pmcu-foot mono">modeled · sensor feed pending integration</div>
      </section>

      {/* ── Transport fleet ── */}
      <section className="pmcu-section">
        <header className="pmcu-h">
          <span className="brief-tag mono">Transport fleet</span>
          {/* These counts are estimates, same as the arterial/parking models —
              without the tag they read as precise registry figures the
              transport office could immediately contradict. */}
          <span className="mono caption" style={{ color: "var(--warn)" }}>ESTIMATED</span>
        </header>
        <ul className="pmcu-fleet">
          {FLEET.map((f) => (
            <li key={f.id} className="pmcu-fleet-row">
              <span className="pmcu-fleet-name">{f.label}</span>
              <span className="mono pmcu-fleet-count">{f.count}</span>
              <span className="pmcu-fleet-unit caption">{f.unit}</span>
              <span className="pmcu-fleet-note caption">{f.note}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* ── Active developments ── */}
      <section className="pmcu-section">
        <header className="pmcu-h">
          <span className="brief-tag mono">Active developments</span>
          <span className="mono caption">municipal pipeline</span>
        </header>
        <ul className="pmcu-dev-list">
          {DEVELOPMENTS.map((d) => (
            <li key={d.id} className="pmcu-dev-item">
              <div className="pmcu-dev-header">
                <span className="pmcu-dev-dot" style={{ background: DEV_COLOR[d.status] }} />
                <span className="pmcu-row-name">{d.name}</span>
                <span className="mono caption" style={{ color: DEV_COLOR[d.status], marginLeft: "auto" }}>
                  {d.status}
                </span>
              </div>
              <div className="pmcu-dev-desc caption">{d.describe}</div>
            </li>
          ))}
        </ul>
        <div className="pmcu-foot mono">municipal pipeline · data: official comms</div>
      </section>
    </div>
  );
}
